/** The Home drag adapter over dnd-kit (SA-1332, lanes spec §4.6): pure, no React or DOM.
 * `keys` is `dragOrder(...).keys`: equal keys are contiguous, and a `:grid` key marks cards drawn side
 * by side, any other key a vertical stack (a lane, or a stretch of full cards). */
import { closestCenter, KeyboardCode, type CollisionDetection, type KeyboardCoordinateGetter, type UniqueIdentifier } from '@dnd-kit/core'
import { rectSortingStrategy, sortableKeyboardCoordinates, verticalListSortingStrategy, type SortingStrategy } from '@dnd-kit/sortable'

const isGrid = (key: string | undefined) => key?.endsWith(':grid') ?? false

/** Other cards shift only inside the dragged card's own key; the dragged card itself follows the pointer,
 * so this never governs it. A stack previews as a vertical list over its OWN slice of rects (SA-1336), so
 * cards of different heights shift by the dragged card's height and the other lane's rects stay out of the
 * gap measurement; a grid run keeps the rect strategy. */
export function laneSortingStrategy(keys: string[]): SortingStrategy {
  return (args) => {
    const { activeIndex, overIndex, index } = args
    const lane = keys[activeIndex]
    if (lane === undefined || keys[overIndex] !== lane || keys[index] !== lane) return null
    if (isGrid(lane)) return rectSortingStrategy(args)
    const start = keys.indexOf(lane)
    const end = keys.lastIndexOf(lane) + 1
    return verticalListSortingStrategy({
      ...args,
      rects: args.rects.slice(start, end),
      activeIndex: activeIndex - start,
      overIndex: overIndex - start,
      index: index - start,
    })
  }
}

/** Closest-center collision over the containers `allowed` admits for the dragged card. */
export function laneCollisions(allowed: (activeId: string, overId: string) => boolean): CollisionDetection {
  return (args) => closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter((c) => allowed(String(args.active.id), String(c.id))),
  })
}

export interface Box { left: number; top: number; width: number; height: number }

const center = (b: Box) => ({ x: b.left + b.width / 2, y: b.top + b.height / 2 })

/** Where to rest a card of `size` that the keyboard moved to `at`, so `closestCenter` names `target` among `others`
 * (SA-1344): `at` itself when its center is already strictly nearest the target, else moved toward the target's
 * center just past the first point where it is (one pixel), never beyond that center. dnd-kit places the card in the
 * target's slot (bottom-aligned with a target after it), which in a column of mixed heights can sit nearer the next card. */
export function aimAt(at: { x: number; y: number }, size: { width: number; height: number }, target: Box, others: Box[]): { x: number; y: number } {
  const c0 = { x: at.x + size.width / 2, y: at.y + size.height / 2 }
  const cT = center(target)
  let t = -1
  for (const other of others) {
    const cO = center(other)
    const d = { x: cO.x - cT.x, y: cO.y - cT.y }
    if (d.x === 0 && d.y === 0) continue
    // A point p is strictly nearer the target than `other` when p·d < k (the two centers' perpendicular bisector).
    const k = (cO.x ** 2 + cO.y ** 2 - cT.x ** 2 - cT.y ** 2) / 2
    const f0 = c0.x * d.x + c0.y * d.y - k
    if (f0 < 0) continue
    // Along c0 → cT that value falls linearly to -|d|²/2 at the target's center, crossing zero at f0 / (f0 + |d|²/2).
    t = Math.max(t, f0 / (f0 + (d.x ** 2 + d.y ** 2) / 2))
  }
  if (t < 0) return at
  const dx = cT.x - c0.x
  const dy = cT.y - c0.y
  const s = Math.min(1, t + 1 / Math.hypot(dx, dy))
  return { x: at.x + s * dx, y: at.y + s * dy }
}

/** dnd-kit's keyboard coordinates over only the containers `allowed` admits, so the preview, `over` and the
 * announcement name the same card (SA-1336), at a resting spot aimed so collision detection names the card dnd-kit
 * chose, whatever the card heights (SA-1344). In a lane or a stretch of full cards Left and Right move nothing
 * (a column has no card beside it); in a grid run they move between the cards of the row. */
export function laneKeyboardCoordinates(
  allowed: (activeId: string, overId: string) => boolean,
  order: { ids: string[]; keys: string[] },
): KeyboardCoordinateGetter {
  return (event, args) => {
    const { active, droppableContainers, collisionRect, droppableRects } = args.context
    if (!active) return sortableKeyboardCoordinates(event, args)
    const activeId = String(active.id)
    const sideways = event.code === KeyboardCode.Left || event.code === KeyboardCode.Right
    if (sideways && !isGrid(order.keys[order.ids.indexOf(activeId)])) {
      event.preventDefault()
      return undefined
    }
    // dnd-kit does not export DroppableContainersMap; these are the two methods sortableKeyboardCoordinates
    // reads (the tests run the real function, so an upgrade that reads more fails them). It asks `get` for the
    // active card, then for the card it chose: the last id asked is the target.
    const asked: UniqueIdentifier[] = []
    const enabled = () => droppableContainers.getEnabled().filter((c) => allowed(activeId, String(c.id)))
    const view = {
      getEnabled: enabled,
      get: (id: UniqueIdentifier) => {
        asked.push(id)
        return droppableContainers.get(id)
      },
    } as unknown as typeof droppableContainers
    const at = sortableKeyboardCoordinates(event, { ...args, context: { ...args.context, droppableContainers: view } })
    const target = asked[asked.length - 1]
    const targetRect = target === undefined ? undefined : droppableRects.get(target)
    if (!at || !collisionRect || !targetRect) return at
    const others = enabled().filter((c) => c.id !== target).flatMap((c) => droppableRects.get(c.id) ?? [])
    return aimAt(at, collisionRect, targetRect, others)
  }
}
